// React Dependencies
import React from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { Box, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { CSS } from '@dnd-kit/utilities';

// Internal Dependencies
import ProgressiveImage from '../common/ProgressiveImage';
import convert from '../../common/convert';

// ============================================================
// SORTABLE PINNED PHOTO
// ============================================================
//
// Used ONLY by Pinned Photos.
//
// Dragging this component changes the display order.
//
// Renders as a responsive square, same aspect-ratio approach
// as PhotoCard, so both panels' grids match visually.
//
// ============================================================

const SortablePinnedPhoto = (props) => {
  const { photo, order, onRemove, photoSize } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(
      transform,
    ),
    transition,
    zIndex:  isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.65 : 1,
    width:   '100%',
  };

  const handleRemove = (event) => {
    event.stopPropagation();
    event.preventDefault();

    onRemove(photo);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <Box
        position="relative"
        width="100%"
        border="1px solid #dddddd"
        borderRadius={4}
        overflow="hidden"
        bgcolor="#fff"
        style={{
          cursor: isDragging
            ? 'grabbing'
            : 'grab',

          boxShadow: isDragging
            ? '0 6px 18px rgba(0,0,0,0.22)'
            : 'none',
        }}
      >
        {/* ==================================================
            SQUARE ASPECT-RATIO WRAPPER
        =================================================== */}

        <Box
          position="relative"
          width="100%"
          style={{ paddingTop: '100%' }}
          bgcolor="#f5f5f5"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
          >
            <ProgressiveImage
              image={convert.imageResizeUrl(
                photo.photoUrl,
                photoSize,
                photoSize,
              )}
              style={{
                width:     '100%',
                height:    '100%',
                objectFit: 'cover',
              }}
              draggable={false}
            />

            {/* ==================================================
                DISPLAY ORDER
                TOP LEFT
            =================================================== */}

            <Box
              position="absolute"
              top={7}
              left={7}
              width={28}
              height={28}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="50%"
              bgcolor="rgba(0,0,0,0.7)"
              color="#fff"
              fontSize={11}
              fontWeight={600}
            >
              {order}
            </Box>

            {/* ==================================================
                REMOVE BUTTON
                TOP RIGHT (previous badge location)
            =================================================== */}

            <IconButton
              size="small"
              data-no-dnd="true"
              onPointerDownCapture={(event) => {
                event.stopPropagation();
              }}
              onClick={handleRemove}
              style={{
                position:        'absolute',
                top:             7,
                right:           7,
                zIndex:          3,
                width:           28,
                height:          28,
                padding:         0,
                backgroundColor:
                  'rgba(255,255,255,0.95)',
                boxShadow:
                  '0 1px 4px rgba(0,0,0,0.25)',
              }}
            >
              <Close
                style={{
                  fontSize: 14,
                  color:    '#777',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

SortablePinnedPhoto.propTypes = {
  photo: PropTypes.shape({
    id:       PropTypes.number.isRequired,
    photoUrl: PropTypes.string.isRequired,
  }),
  order:     PropTypes.number.isRequired,
  onRemove:  PropTypes.func.isRequired,
  photoSize: PropTypes.number,
};

SortablePinnedPhoto.defaultProps = {
  photo: {
    id:       1,
    photoUrl: 'https://example.com',
  },
  photoSize: 300,
};

export default SortablePinnedPhoto;
